import { useState, useEffect, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";

const TYPE_ORDER = ["original", "annotated", "heatmap", "area"];

const TYPE_BG = {
  original: "#dbeafe",
  annotated: "#dcfce7",
  heatmap: "#fef9c3",
  area: "#fce7f3",
};
const TYPE_COLOR = {
  original: "#1d4ed8",
  annotated: "#15803d",
  heatmap: "#854d0e",
  area: "#9d174d",
};

function parseSr(fileName = "") {
  const clean = fileName
    .replace(/_updated_\d{8}(?:_\d{6})?\./i, ".")
    .replace(/_updated\./i, ".");
  const m = clean.match(/_[A-Za-z]+_(\d+)_(external|internal)\.\w+$/i);
  return m ? parseInt(m[1], 10) : null;
}

function parseType(fileName = "") {
  const low = fileName.toLowerCase();
  for (const t of TYPE_ORDER) if (low.includes(t)) return t;
  return "other";
}

function parseLabel(fileName = "") {
  const clean = fileName
    .replace(/_updated_\d{8}(?:_\d{6})?\./i, ".")
    .replace(/_updated\./i, ".");
  const m = clean.match(/_([A-Za-z]+)_(\d+)_(external|internal)\.\w+$/i);
  if (!m) return fileName.replace(/\.\w+$/, "");
  const type = parseType(fileName);
  const side = m[1].toUpperCase();
  const pos = m[3].toLowerCase();
  return `${type} · ${side} · ${pos}`;
}

function groupAllBySr(allImages = []) {
  const map = new Map();

  allImages.forEach((img) => {
    const sr = parseSr(img.file_name);
    const key = sr === null ? 0 : sr;
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(img);
  });

  // sort images within each group: original → annotated → heatmap → area
  map.forEach((imgs) => {
    imgs.sort((a, b) => {
      const ta = TYPE_ORDER.indexOf(parseType(a.file_name));
      const tb = TYPE_ORDER.indexOf(parseType(b.file_name));
      if (ta !== tb) return ta - tb;
      return a.file_name.localeCompare(b.file_name);
    });
  });

  // sort groups ascending (sr 0 / "other" goes last)
  return Array.from(map.entries())
    .sort((a, b) => {
      if (a[0] === 0) return 1;
      if (b[0] === 0) return -1;
      return a[0] - b[0];
    })
    .map(([sr, imgs]) => ({ sr, imgs }));
}

// ─────────────────────────────────────────────────────────────────────────────

export default function SlideViewModal({ open, onClose, allImages = [] }) {
  const groups = groupAllBySr(allImages);
  const totalGrps = groups.length;

  const [srIdx, setSrIdx] = useState(0);
  const [mode, setMode] = useState("grid");
  const [imgIdx, setImgIdx] = useState(0);

  useEffect(() => {
    if (open) {
      setSrIdx(0);
      setMode("grid");
      setImgIdx(0);
    }
  }, [open]);

  const group = groups[srIdx] ?? { sr: 0, imgs: [] };
  const totalImgs = group.imgs.length;
  const curImg = group.imgs[imgIdx];
  const srLabel = group.sr === 0 ? "Other" : `Sr. ${group.sr}`;

  const goSr = (i) => {
    setSrIdx(i);
    setMode("grid");
    setImgIdx(0);
  };
  const prevSr = () => goSr(Math.max(0, srIdx - 1));
  const nextSr = () => goSr(Math.min(totalGrps - 1, srIdx + 1));
  const prevImg = () => setImgIdx((p) => Math.max(0, p - 1));
  const nextImg = () => setImgIdx((p) => Math.min(totalImgs - 1, p + 1));

  const openSingle = (i) => {
    setImgIdx(i);
    setMode("single");
  };
  const backGrid = () => setMode("grid");

  const handleKey = useCallback(
    (e) => {
      if (!open) return;
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (mode === "grid") {
        if (e.key === "ArrowRight") nextSr();
        if (e.key === "ArrowLeft") prevSr();
      } else {
        if (e.key === "ArrowRight") nextImg();
        if (e.key === "ArrowLeft") prevImg();
        if (e.key === "Backspace") backGrid();
      }
    },
    [open, mode, srIdx, imgIdx, totalGrps, totalImgs],
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  if (!open || totalGrps === 0) return null;

  return (
    <Dialog
      open={open}
      onClose={(event, reason) => {
        if (reason === "backdropClick") return;
        onClose();
      }}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: "92vh",
          borderRadius: "12px",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      {/* ── HEADER ──────────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 16px",
          borderBottom: "1px solid #e5e7eb",
          flexShrink: 0,
          background: "#fff",
          gap: 8,
        }}
      >
        {/* left */}
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              fontSize: 12,
              fontFamily: "monospace",
              fontWeight: 700,
              background: "#eff6ff",
              border: "1px solid #bfdbfe",
              borderRadius: 6,
              padding: "3px 10px",
              color: "#1d4ed8",
            }}
          >
            {srLabel}
          </span>
          {mode === "single" && (
            <span
              style={{
                fontSize: 12,
                color: "#6b7280",
                fontFamily: "monospace",
              }}
            >
              {imgIdx + 1} / {totalImgs}
            </span>
          )}
        </div>

        {/* right controls */}
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <IconButton
            size="small"
            onClick={onClose}
            sx={{
              border: "1px solid #e5e7eb",
              borderRadius: "6px",
              width: 32,
              height: 32,
            }}
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </div>
      </div>

      {/* ── BODY ────────────────────────────────────────────────────────── */}
      <div
        style={{
          flex: 1,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* GRID VIEW */}
        {mode === "grid" && (
          <div
            style={{
              flex: 1,
              overflowY: "auto",
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(175px, 1fr))",
              gap: 10,
              padding: 16,
            }}
          >
            {group.imgs.map((img, i) => {
              const type = parseType(img.file_name);
              return (
                <div
                  key={i}
                  onClick={() => openSingle(i)}
                  style={{
                    borderRadius: 8,
                    overflow: "hidden",
                    border: "1px solid #e5e7eb",
                    cursor: "pointer",
                    position: "relative",
                    transition: "border-color 0.12s, transform 0.1s",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.borderColor = "#2563eb";
                    e.currentTarget.style.transform = "scale(1.015)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.borderColor = "#e5e7eb";
                    e.currentTarget.style.transform = "scale(1)";
                  }}
                >
                  <img
                    src={img.file_url}
                    alt={img.file_name}
                    loading="lazy"
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                  {/* type badge */}
                  <div
                    style={{
                      position: "absolute",
                      top: 6,
                      left: 6,
                      background: TYPE_BG[type] || "#f3f4f6",
                      color: TYPE_COLOR[type] || "#374151",
                      fontSize: 9,
                      fontFamily: "monospace",
                      fontWeight: 700,
                      padding: "2px 6px",
                      borderRadius: 4,
                      textTransform: "uppercase",
                    }}
                  >
                    {type}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* SINGLE VIEW */}
        {mode === "single" && curImg && (
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              minHeight: 0,
            }}
          >
            {/* stage */}
            <div
              style={{
                flex: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                maxHeight: "100%",
              }}
            >
              <Zoom>
                <img
                  src={curImg.file_url}
                  alt={curImg.file_name}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "calc(92vh - 100px)",
                    objectFit: "contain",
                    display: "block",
                  }}
                />
              </Zoom>

              {imgIdx > 0 && (
                <button onClick={prevImg} style={arrowStyle("left")}>
                  ‹
                </button>
              )}
              {imgIdx < totalImgs - 1 && (
                <button onClick={nextImg} style={arrowStyle("right")}>
                  ›
                </button>
              )}
              <div
                style={{
                  position: "absolute",
                  bottom: 10,
                  left: "50%",
                  transform: "translateX(-50%)",
                  background: "rgba(0,0,0,0.5)",
                  color: "#fff",
                  fontSize: 11,
                  fontFamily: "monospace",
                  padding: "3px 12px",
                  borderRadius: 20,
                }}
              >
                {imgIdx + 1} / {totalImgs}
              </div>
            </div>

            {/* filmstrip */}
            <div
              style={{
                display: "flex",
                gap: 6,
                padding: "8px 12px",
                borderTop: "1px solid #e5e7eb",
                overflowX: "auto",
                flexShrink: 0,
              }}
            >
              {group.imgs.map((img, i) => {
                const type = parseType(img.file_name);
                return (
                  <div
                    key={i}
                    onClick={() => setImgIdx(i)}
                    title={parseLabel(img.file_name)}
                    style={{
                      width: 60,
                      height: 48,
                      borderRadius: 6,
                      overflow: "hidden",
                      flexShrink: 0,
                      position: "relative",
                      border: `2px solid ${i === imgIdx ? "#2563eb" : "transparent"}`,
                      opacity: i === imgIdx ? 1 : 0.5,
                      cursor: "pointer",
                      transition: "border-color 0.12s, opacity 0.12s",
                    }}
                  >
                    <img
                      src={img.file_url}
                      alt=""
                      loading="lazy"
                      style={{
                        width: "100%",
                        height: "100%",
                        objectFit: "cover",
                        display: "block",
                      }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        bottom: 0,
                        left: 0,
                        right: 0,
                        background: TYPE_BG[type] || "#f3f4f6",
                        color: TYPE_COLOR[type] || "#374151",
                        fontSize: 7,
                        fontFamily: "monospace",
                        fontWeight: 700,
                        padding: "1px 3px",
                        textAlign: "center",
                        textTransform: "uppercase",
                      }}
                    >
                      {type}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── FOOTER ──────────────────────────────────────────────────────── */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "10px 16px",
          borderTop: "1px solid #e5e7eb",
          flexShrink: 0,
          background: "#fafafa",
        }}
      >
        <button
          onClick={prevSr}
          disabled={srIdx === 0}
          style={footBtnStyle(srIdx === 0)}
          onMouseEnter={(e) => {
            if (srIdx > 0) e.currentTarget.style.background = "#f3f4f6";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "none";
          }}
        >
          ← Prev Sr.
        </button>
        <span
          style={{ fontSize: 12, color: "#6b7280", fontFamily: "monospace" }}
        >
          {srLabel} &nbsp;·&nbsp; {srIdx + 1} / {totalGrps}
        </span>
        <button
          onClick={nextSr}
          disabled={srIdx === totalGrps - 1}
          style={footBtnStyle(srIdx === totalGrps - 1)}
          onMouseEnter={(e) => {
            if (srIdx < totalGrps - 1)
              e.currentTarget.style.background = "#f3f4f6";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "none";
          }}
        >
          Next Sr. →
        </button>
      </div>
    </Dialog>
  );
}

function arrowStyle(side) {
  return {
    position: "absolute",
    [side]: 14,
    top: 0,
    bottom: 0,
    margin: "auto 0",
    width: 40,
    height: 40,
    borderRadius: "50%",
    background: "rgba(0,0,0,0.5)",
    color: "#fff",
    border: "none",
    cursor: "pointer",
    outline: "none",   
    fontSize: 28,
    display: "flex",
    justifyContent: "center",
    transition: "background 0.12s",
    lineHeight: 1,
  };
}

function footBtnStyle(disabled) {
  return {
    fontSize: 12,
    padding: "6px 14px",
    border: "1px solid #d1d5db",
    borderRadius: 7,
    background: "none",
    cursor: disabled ? "not-allowed" : "pointer",
    color: "black",
    opacity: disabled ? 0.3 : 1,
    transition: "background 0.1s",
  };
}
